import type { ApmBase } from "@elastic/apm-rum";
import { UserContext, TenantContext, ProfileContext, TraceContextHeaders } from "../../interfaces/apm-server";

type LabelValue = string | number | boolean;

function toLabelValue(value: unknown): LabelValue | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return value;
  return String(value);
}

function cleanLabels(input: Record<string, unknown>): Record<string, LabelValue> {
  const out: Record<string, LabelValue> = {};
  for (const [k, v] of Object.entries(input)) {
    const lv = toLabelValue(v);
    if (lv !== undefined) out[k] = lv;
  }
  return out;
}

export class ApmContextService {
  private lastTenantId?: string;
  private lastProfileId?: string;

  constructor(private readonly apm: ApmBase) {}

  private isActive(): boolean {
    return !!this.apm?.isActive?.();
  }

  getTraceContextHeaders(): TraceContextHeaders {
    return {
      tenantId: this.lastTenantId,
      profileId: this.lastProfileId,
    };
  }

  clearUserContext(): void {
    if (!this.isActive()) return;
    // apm-rum typings don't expose a clearUserContext() method; reset to empty user.
    this.apm.setUserContext({});
  }

  setUserContext(user: UserContext): void {
    if (!this.isActive()) return;
    this.apm.setUserContext({ id: user.id, username: user.username, email: user.email });
    this.apm.addLabels(cleanLabels({ "user.id": user.id, "user.name": user.username }));
  }

  setTenantContext(tenant: TenantContext): void {
    this.lastTenantId = tenant.id;
    if (!this.isActive()) return;
    this.apm.addLabels(
      cleanLabels({
        tenantId: tenant.id,
        tenantName: tenant.name,
        tenantPlan: tenant.plan,
        "tracestate.tenant": tenant.id,
      })
    );
  }

  setProfileContext(profile: ProfileContext): void {
    this.lastProfileId = profile.id;
    if (!this.isActive()) return;
    this.apm.addLabels(cleanLabels({ profileId: profile.id }));
  }

  addLabels(labels: Record<string, unknown>): void {
    if (!this.isActive()) return;
    this.apm.addLabels(cleanLabels(labels));
  }

  startTransaction(name: string, type: string) {
    if (!this.isActive()) return undefined;
    return this.apm.startTransaction(name, type);
  }

  captureError(error: unknown, options?: any): void {
    if (!this.isActive()) return;
    this.apm.captureError(error as any, options);
  }
}

